import { useCallback, useState, useMemo, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle, Volume2, VolumeX, Video, VideoOff } from 'lucide-react';
import { MapView } from '../components/map/MapView';
import type { NarrativeMeta } from '../components/map/CameraController';
import { NarrativeOverlay, AlarmOverlay, isTTSEnabled, setTTSEnabled } from '../components/map/NarrativeOverlay';
import { TrafficSignalDot } from '../components/map/TrafficSignalDot';
import { IntersectionMarkers } from '../components/map/IntersectionMarkers';
import { useMapZoom } from '../components/map/MapBearingContext';
import { CabinetMarker } from '../components/map/CabinetMarker';
import { FloatingPanel } from '../components/map/FloatingPanel';
import { PhaseTimeline } from '../components/map/PhaseTimeline';
import { AlarmPanel, useAlarmCounts } from '../components/guadalajara/AlarmPanel';
import { useIntersectionState } from '../hooks/useIntersectionState';
import { usePlanSchedule } from '../hooks/usePlanSchedule';
import { useDetailedSimulation } from '../store/detailedSimulation';
import { PLAN_CONFIGS } from '../data/planSchedule';
import {
  santaColomaStaticSignals,
  SANTA_COLOMA_BOUNDS,
  SANTA_COLOMA_CENTER,
  SANTA_COLOMA_ZOOM,
  SANTA_COLOMA_BEARING,
  SANTA_COLOMA_PITCH,
  SANTA_COLOMA_REALISTIC_CENTER,
  SANTA_COLOMA_REALISTIC_ZOOM,
  SANTA_COLOMA_REALISTIC_BEARING,
  SANTA_COLOMA_REALISTIC_PITCH,
} from '../data/santa-coloma';
// Datos reales de ARS93 — 78 reguladores de Santa Coloma (ver reguladores-scg.ts).
import { santaColomaIntersections } from '../data/santa-coloma';
import type { IntersectionDefinition } from '../types/intersection';
import { resolveEstado } from '../data/estados-regulador';
import { toDisplayId } from '../utils/canonicalId';
import { DemoHighlights } from '../components/guadalajara/HighlightRing';
import { ControlPanel } from '../components/control/ControlPanel';
import { RegulatorEditPanel } from '../components/control/RegulatorEditPanel';
import { AddEntityPanel } from '../components/control/AddEntityPanel';
import { AlarmCatalogPanel } from '../components/AlarmCatalogPanel';
import { EditGhostOverlay } from '../components/map/EditGhostOverlay';
import { useEditMode } from '../store/editMode';
import { useDemoTour } from '../store/demoTour';
import { useCameraControl } from '../store/cameraControl';
import { useAssistantPanel } from '../store/assistantPanel';
import { useCommandConsole } from '../store/commandConsole';
import { useMapMode } from '../store/mapMode';
import { useMapCamera } from '../store/mapCamera';
import { useOverridesVersion } from '../store/overridesVersion';
import { RealisticMapView } from '../components/map/RealisticMapView';
import {
  mountRealisticCabinetMarkers,
  mountRealisticGroupTrafficLights,
} from '../components/map/realisticCabinetMarkers';
import { mountRealisticTrafficLayer } from '../components/map/realisticTrafficLayer';
import { RealisticPinControlsPanel } from '../components/map/RealisticPinControlsPanel';

// Zoom mínimo para dibujar los grupos de cada cruce. Por debajo solo se
// muestra el cabinet (coherente con el detalle de TrafficSignalDot a z17).
// Evita montar ~4 markers DOM extra por regulador en la vista de municipio.
const GROUP_ZOOM_THRESHOLD = 17;

// Radio (grados, equirectangular barato) dentro del cual un regulador
// muestra sus grupos detallados a zoom alto. Sin este filtro, a z>=17 se
// montaban los grupos de los 79 reguladores a la vez — con 16 grupos cada
// uno (ampliado 2026-09-14) son 1264 marcadores 3D animados simultáneos,
// suficiente para colgar/crashear el navegador. ~700 m de radio cubre el
// viewport visible en este rango de zoom con margen.
const GROUP_RENDER_RADIUS_SQ = 0.007 * 0.007;

function isNearMapCenter(point: [number, number], mapCenter: [number, number]): boolean {
  const dx = point[0] - mapCenter[0];
  const dy = point[1] - mapCenter[1];
  return dx * dx + dy * dy < GROUP_RENDER_RADIUS_SQ;
}

/** Renderiza un regulador: markers de grupo + cabinet, con datos MQTT reales */
function SimulatedRegulator({
  intersection,
  alarmCount = 0,
  forceOpen = false,
  mapCenter,
}: {
  intersection: IntersectionDefinition;
  alarmCount?: number;
  forceOpen?: boolean;
  mapCenter: [number, number];
}) {
  const mapZoom = useMapZoom();
  const {
    groupColors,
    currentPhase,
    cyclePosition,
    cycleTime,
    plan,
    estado,
    online,
  } = useIntersectionState(intersection, 'mqtt');
  const estadoVisual = resolveEstado(estado, online);

  return (
    <>
      {/* Grupos solo a zoom alto y cerca del centro visible: a vista de
          municipio son ruido visual, y lejos del viewport son coste de
          render puro. El cabinet se muestra siempre. */}
      {mapZoom >= GROUP_ZOOM_THRESHOLD && isNearMapCenter(intersection.center, mapCenter) && (
        <IntersectionMarkers
          cabinetId={intersection.cabinet.logicalId}
          groups={intersection.groups}
          groupColors={groupColors}
        />
      )}
      <CabinetMarker
        cabinet={intersection.cabinet}
        estado={estadoVisual}
        isConnected={online}
        currentPlan={plan}
        currentPhase={currentPhase}
        cyclePosition={cyclePosition}
        cycleTime={cycleTime}
        groupCount={intersection.groups.length}
        detectorCount={0}
        alarmCount={alarmCount}
        forceOpen={forceOpen}
      />
    </>
  );
}

/** Cycle panel — only rendered when near a regulator */
function CyclePanel({
  intersection,
  phases,
  alarmCount,
}: {
  intersection: IntersectionDefinition;
  phases: { id: string; duration: number }[];
  alarmCount: number;
}) {
  const { currentPhase, cyclePosition, cycleTime, plan } = useIntersectionState(intersection, 'mqtt');
  const planLabel = PLAN_CONFIGS[plan]?.label ?? '';
  const id = intersection.cabinet.logicalId;
  const nVeh = intersection.groups.filter(g => g.type === 'V').length;
  const nPed = intersection.groups.filter(g => g.type === 'P').length;

  return (
    <FloatingPanel
      title={intersection.name}
      position="inline"
      highlightId="cycle-panel"
      icon={<span className={`text-xs font-mono font-bold ${REG_TEXT_COLOR}`}>{toDisplayId(id)}</span>}
    >
      <div className="flex items-stretch gap-0 min-w-[420px] sm:min-w-[520px]">
        {/* Left: regulator info */}
        <div className="flex flex-col justify-center gap-1 px-3 py-2 border-r border-gray-700/30">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-500">Plan</span>
            <span className="text-[11px] font-mono font-medium text-cyan-400">P{plan}</span>
            <span className="text-[10px] text-gray-500">{planLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500">Grupos</span>
            <span className="text-[11px] font-mono text-gray-300">{nVeh}V + {nPed}P</span>
          </div>
        </div>
        {/* Center: phase timeline */}
        <div className="flex-1">
          <PhaseTimeline
            phases={phases}
            currentPhase={currentPhase}
            cyclePosition={cyclePosition}
            cycleTime={cycleTime}
          />
        </div>
        {/* Right: alarm badge */}
        <div className="flex items-center gap-1 px-3 border-l border-gray-700/30">
          {alarmCount > 0 ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              <span className="text-[11px] font-mono font-medium text-red-400">{alarmCount}</span>
            </>
          ) : (
            <CheckCircle className="w-3.5 h-3.5 text-green-400" />
          )}
        </div>
      </div>
    </FloatingPanel>
  );
}

// Color uniforme de los reguladores de la red (antes había 1 color por id,
// cuando solo existían 2 reguladores placeholder).
const REG_TEXT_COLOR = 'text-cyan-400';

/** Overview bar — resumen de la red, mostrado con el mapa alejado */
function OverviewBar({ alarmCounts }: { alarmCounts: Record<number, number> }) {
  const total = santaColomaIntersections.length;
  const inAlarm = santaColomaIntersections.filter(
    ix => (alarmCounts[ix.cabinet.logicalId] || 0) > 0,
  ).length;
  return (
    <div data-highlight="overview-bar">
      <div className="bg-gray-900/90 backdrop-blur-md border border-gray-700/50 rounded-lg shadow-2xl px-4 py-2 flex items-center gap-3">
        <span className="text-[11px] font-mono text-gray-300">{total} reguladores</span>
        <span className="w-px h-3 bg-gray-700/70" />
        {inAlarm > 0 ? (
          <span className="flex items-center gap-1.5 text-[11px] font-mono text-red-400">
            <AlertTriangle className="w-3 h-3" /> {inAlarm} en alarma
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] font-mono text-green-400">
            <CheckCircle className="w-3.5 h-3.5" /> todos operativos
          </span>
        )}
      </div>
    </div>
  );
}

/** Find nearest intersection to map center (returns null if too far or zoomed out) */
function findNearbyIntersection(
  center: [number, number],
  zoom: number,
): IntersectionDefinition | null {
  if (zoom < 15.5) return null;
  let best: IntersectionDefinition | null = null;
  let bestDist = Infinity;
  for (const ix of santaColomaIntersections) {
    const dx = center[0] - ix.center[0];
    const dy = center[1] - ix.center[1];
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = ix;
    }
  }
  // ~0.004° ≈ 400m threshold
  if (bestDist > 0.004 * 0.004) return null;
  return best;
}

interface SantaColomaMapViewProps {
  alarms: import('../components/guadalajara/AlarmPanel').MockAlarm[];
}

export function SantaColomaMapView({ alarms }: SantaColomaMapViewProps) {
  // Plan schedule — drives plan changes based on real clock
  const { clock, activePlan } = usePlanSchedule();
  const phaseDurations = useDetailedSimulation(s => s.phaseDurations);
  const currentPlan = useDetailedSimulation(s => s.currentPlan);
  const cycleTime = useDetailedSimulation(s => s.cycleTime);

  // Dynamic phases from store (updated when plan changes)
  const phases = useMemo(() => [
    { id: 'F1', duration: phaseDurations[0] },
    { id: 'F2', duration: phaseDurations[1] },
    { id: 'F3', duration: phaseDurations[2] },
    { id: 'F4', duration: phaseDurations[3] },
  ], [phaseDurations]);

  // Track which regulator is near the map center
  const [nearbyIntersection, setNearbyIntersection] = useState<IntersectionDefinition | null>(null);
  const [cameraDebug, setCameraDebug] = useState<{ center: [number, number]; zoom: number; bearing: number; pitch: number } | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  const handleViewState = useCallback((center: [number, number], zoom: number, bearing: number, pitch: number) => {
    setNearbyIntersection(findNearbyIntersection(center, zoom));
    setCameraDebug({ center, zoom, bearing, pitch });
    // Persistir en el store para que el botón de cambio de vista re-monte
    // la otra cámara en la misma posición.
    useMapCamera.getState().setView(center, zoom, bearing, pitch);
  }, []);

  // Snapshot del store al montar — no reaccionamos a cambios posteriores
  // (las vistas son uncontrolled tras el mount; el SDK guarda la cámara).
  // Cada vez que el usuario cambia de modo, este componente se evalúa de
  // nuevo: el useMemo recoge el último estado del store al cambiar `mapMode`.
  const mapMode = useMapMode(s => s.mode);
  const editModeActive = useEditMode(s => s.active);
  // Re-render al guardar un cambio de Modo Edición (los datasets se mutan
  // in-place en overridesRuntime.ts, no disparan re-render por sí solos).
  useOverridesVersion(s => s.version);
  const initialCam = useMemo(() => {
    const cam = useMapCamera.getState();
    return {
      center: cam.center ?? (mapMode === 'realistic' ? SANTA_COLOMA_REALISTIC_CENTER : SANTA_COLOMA_CENTER),
      zoom: cam.zoom ?? (mapMode === 'realistic' ? SANTA_COLOMA_REALISTIC_ZOOM : SANTA_COLOMA_ZOOM),
      bearing: cam.bearing ?? (mapMode === 'realistic' ? SANTA_COLOMA_REALISTIC_BEARING : SANTA_COLOMA_BEARING),
      pitch: cam.pitch ?? (mapMode === 'realistic' ? SANTA_COLOMA_REALISTIC_PITCH : SANTA_COLOMA_PITCH),
    };
  }, [mapMode]);

  // Atajos de teclado: 'd' overlay debug, 'c' control automático cámara,
  // 'i' enfoca el buscador del header (SearchBox), 'a' o 'q' pliegan y
  // despliegan el asistente.
  // Capture phase para llegar antes que el canvas de MapLibre, que hace
  // stopPropagation sobre algunas teclas y bloquearía el listener normal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'd') setShowDebug(prev => !prev);
      if (e.key === 'c') useCameraControl.getState().toggleAutoMove();
      if (e.key === 'a' || e.key === 'q') useAssistantPanel.getState().toggle();
      if (e.key === 'i') {
        // Sin esto la propia "i" se escribiría en el buscador recién enfocado.
        e.preventDefault();
        useCommandConsole.getState().toggle();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, []);

  const alarmCounts = useAlarmCounts(alarms);
  const demoHighlights = useDemoTour(s => s.highlights);
  const cabinetPanelsOpen = demoHighlights.includes('cabinet-panels');

  const [alarmMeta, setAlarmMeta] = useState<NarrativeMeta | null>(null);
  const [ttsOn, setTtsOn] = useState(isTTSEnabled);
  const autoMove = useCameraControl(s => s.autoMove);

  // Format next change time
  const nextChangeLabel = activePlan.nextChange
    ? `${String(activePlan.nextChange.hour).padStart(2, '0')}:${String(activePlan.nextChange.minute).padStart(2, '0')} → P${activePlan.nextChange.plan}`
    : null;

  return (
    <div className="relative w-full h-full">
      <div className="absolute inset-0 map-container">
        {mapMode === 'realistic' ? (
          <RealisticMapView
            center={initialCam.center}
            zoom={initialCam.zoom}
            bearing={initialCam.bearing}
            pitch={initialCam.pitch}
            onViewState={handleViewState}
            onMapReady={(mapEl) => {
              const opts = { intersections: santaColomaIntersections };
              const cleanupCabinets = mountRealisticCabinetMarkers(mapEl, opts);
              const cleanupGroups = mountRealisticGroupTrafficLights(mapEl, opts);
              const cleanupTraffic = mountRealisticTrafficLayer(mapEl);
              return () => {
                cleanupCabinets();
                cleanupGroups();
                cleanupTraffic();
              };
            }}
          />
        ) : (
          <MapView
            center={initialCam.center}
            zoom={initialCam.zoom}
            cameraControl
            onCameraAlarm={undefined}
            onAlarmMeta={setAlarmMeta}
            onViewState={handleViewState}
            bearing={initialCam.bearing}
            pitch={initialCam.pitch}
            maxBounds={SANTA_COLOMA_BOUNDS}
            minZoom={13}
            maxZoom={19}
          >
            {santaColomaStaticSignals.map(signal => (
              <TrafficSignalDot key={signal.id} signal={signal} />
            ))}

            {santaColomaIntersections.map(intersection => (
              <SimulatedRegulator
                key={intersection.id}
                intersection={intersection}
                alarmCount={alarmCounts[intersection.cabinet.logicalId] || 0}
                forceOpen={cabinetPanelsOpen}
                mapCenter={cameraDebug?.center ?? initialCam.center}
              />
            ))}

            <EditGhostOverlay />
          </MapView>
        )}
      </div>

      {/* Narrativa top-center + anuncio por voz (speakNarrative vive en su
          useEffect) — sin este componente montado el botón de altavoz no
          tiene ningún efecto, aunque cambie de estado visualmente. */}
      <NarrativeOverlay meta={alarmMeta} />

      {/* Alarm overlay — camera-injected, independent from other panels */}
      <AlarmOverlay meta={alarmMeta} />

      {/* Barra inferior: alarmas a la izquierda, ciclo/resumen en el centro.
          Grid 1fr/auto/1fr — el panel central queda centrado de verdad
          mientras hay sitio y solo se desplaza cuando el de alarmas necesita
          más ancho; nunca se pisan (antes eran absolute independientes y por
          debajo de ~1200 px el de ciclo tapaba los botones de alarmas). */}
      <div className="absolute bottom-3 inset-x-3 z-10 pointer-events-none flex flex-col-reverse items-start gap-2 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-end lg:gap-3">
        <div className="pointer-events-auto lg:justify-self-start">
          <AlarmPanel alarms={alarms} highlightId="alarm-panel" />
        </div>
        <div className="pointer-events-auto">
          {nearbyIntersection ? (
            <CyclePanel
              intersection={nearbyIntersection}
              phases={phases}
              alarmCount={alarmCounts[nearbyIntersection.cabinet.logicalId] || 0}
            />
          ) : (
            <OverviewBar alarmCounts={alarmCounts} />
          )}
        </div>
        <div />
      </div>

      {/* TTS toggle */}
      <button
        data-highlight="tts-button"
        onClick={() => { setTTSEnabled(!ttsOn); setTtsOn(!ttsOn); }}
        className={`absolute top-3 right-[170px] sm:right-[220px] z-40 p-2 rounded-lg border backdrop-blur-sm transition-colors ${ttsOn ? 'bg-cyan-900/50 border-cyan-600/50 text-cyan-400' : 'bg-gray-900/90 border-gray-700 text-gray-500 hover:text-gray-300'}`}
        title={ttsOn ? 'Desactivar narración por voz' : 'Activar narración por voz'}
      >
        {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      {/* Control automático de cámara — toggle (atajo: tecla C) */}
      <button
        onClick={() => useCameraControl.getState().toggleAutoMove()}
        className={`absolute top-3 right-[214px] sm:right-[268px] z-40 p-2 rounded-lg border backdrop-blur-sm transition-colors ${autoMove ? 'bg-cyan-900/50 border-cyan-600/50 text-cyan-400' : 'bg-amber-900/50 border-amber-600/50 text-amber-400'}`}
        title={autoMove
          ? 'Encuadre automático de cámara activo — pulsa C para pasar a control manual'
          : 'Cámara en modo manual — no se moverá por alarmas. Pulsa C para reactivar'}
      >
        {autoMove ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
      </button>

      {/* Clock + Plan badge */}
      <div data-highlight="clock-badge" className="absolute top-3 right-3 bg-gray-900/90 backdrop-blur-sm rounded-lg border border-gray-700 px-2 py-1.5 sm:px-3 sm:py-2 z-40">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-400" />
            <span className="text-xs sm:text-sm font-mono font-bold text-white">{clock}</span>
          </div>
          <span className="text-[10px] text-gray-500 hidden sm:inline">Santa Coloma</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 sm:mt-1">
          <span className="text-[11px] sm:text-xs font-mono font-medium text-cyan-400">
            P{currentPlan}
          </span>
          <span className="text-[10px] text-gray-500 hidden sm:inline">
            {PLAN_CONFIGS[currentPlan]?.label}
          </span>
          <span className="text-[10px] text-gray-400">
            {(cycleTime / 10).toFixed(0)}s
          </span>
        </div>
        {nextChangeLabel && (
          <div className="text-[10px] text-gray-500 mt-0.5 hidden sm:block">
            próx: {nextChangeLabel}
          </div>
        )}
      </div>

      {/* Camera debug overlay — toggle with 'd' key */}
      {showDebug && cameraDebug && (
        <div className="absolute top-14 left-3 z-50 bg-black/80 backdrop-blur-sm rounded-lg border border-gray-600 px-3 py-2 font-mono text-[11px] text-green-400 select-all">
          <div className="text-gray-500 text-[9px] mb-1">Camera (press d to hide)</div>
          <div>center: [{cameraDebug.center[0].toFixed(4)}, {cameraDebug.center[1].toFixed(4)}]</div>
          <div>zoom: {cameraDebug.zoom.toFixed(1)}</div>
          <div>bearing: {cameraDebug.bearing.toFixed(1)}</div>
          <div>pitch: {cameraDebug.pitch.toFixed(1)}</div>
        </div>
      )}

      {/* Panel del regulador seleccionado: edición (admin, Modo Edición) u operación */}
      {editModeActive ? <RegulatorEditPanel /> : <ControlPanel />}

      {/* Añadir regulador/semáforo (ARS-05) — no requiere selección previa */}
      {editModeActive && <AddEntityPanel />}

      {/* Chuleta de códigos de alarma — disponible para todos los operadores */}
      <AlarmCatalogPanel />

      {/* Demo highlight overlays — zero layout impact */}
      <DemoHighlights />

      {/* Sliders de control de pins (solo en vista realista) */}
      {mapMode === 'realistic' && <RealisticPinControlsPanel />}
    </div>
  );
}
